import React, { useId, useMemo } from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import NetRisePlainLogo from '../../NRIcons/NetRisePlainLogo';
import NRHighlightedText from '../NRHighlightedText/NRHighlightedText';
import { CONSTANTS } from '../../constants/constants';
import { getWeightedCategory, getWeightedColor, getWeightedColorAlt, isNumber } from '../../utils/utils';
import NRSummaryDonutContainer from '../NRCharts/NRSummaryDonut/NRSummaryDonutContainer';
import NRComparisonSBOMBox from '../NRReportComparison/NRComparisonSBOMBox';
import NRLineChart from '../NRCharts/NRLineChart/NRLineChart';
import NRCryptographyChart2 from '../NRCryptographyChart/NRCryptographyChart2';
import NRMisconfigurationChecks from '../NRCharts/NRMisconfigurationChecks/NRMisconfigurationChecks';
import { CrackedCredentialsCards, SectionTable } from './NRAssetSummaryPrintableSections';
import { buildKnownExploitsChartConfig, valueOrFallback } from './NRAssetSummaryPrintable.utils';

const sectionTitleStyles = theme => ({
  mb: 2,
  borderBottom: `2px solid ${theme.palette.divider}`,
  pb: 1
});

const NRAssetSummaryPrintable = React.forwardRef(
  (
    {
      asset,
      vulnSummary,
      exploits,
      crypto,
      misconfig,
      misconfigByCat = [],
      topVulnsByEPSS = [],
      topThreatActorWeaponized = [],
      topVulnerableComponents = [],
      licenses = [],
      crackedCredentials = [],
      generatedAt
    },
    ref
  ) => {
    const theme = useTheme();
    const riskValue = asset?.risk?.score;
    const weightedCategory = getWeightedCategory(riskValue);
    const weightedColor = getWeightedColor(!isNumber(riskValue) ? 0 : riskValue, theme);
    const weightedColorAlt = getWeightedColorAlt(!isNumber(riskValue) ? 0 : riskValue, theme);

    const printableGeneratedAt = useMemo(() => generatedAt || new Date().toLocaleString(), [generatedAt]);

    // `useId()` returns `:r0:`-style IDs. Colons are not valid in CSS selectors,
    // and `useChartA11y` (downstream) builds a selector from this id, so we
    // strip them to keep the resulting id selector-safe.
    const knownExploitsChartContainerId = useId().replace(/:/g, '');
    const misconfigChartContainerId = useId().replace(/:/g, '');

    const misconfigChartProps = useMemo(
      () => ({
        failed: Number(misconfig?.failed ?? 0),
        passed: Number(misconfig?.passed ?? 0),
        notApplicable: Number(misconfig?.notApplicable ?? 0)
      }),
      [misconfig]
    );

    const { knownExploitsChart, knownExploitsChartHeight } = useMemo(() => {
      const chart = buildKnownExploitsChartConfig(exploits);
      const height = Math.min(520, Math.max(220, (chart.categories?.length || 0) * 44));
      return { knownExploitsChart: chart, knownExploitsChartHeight: height };
    }, [exploits]);

    // In dark mode `primary.main` can be ~black (see `customTheme`), which can make bar fills
    // visually disappear against a dark plot background. Prefer a bright token as fallback.
    const knownExploitsBarColors = [theme.palette.customPalette?.[0] || theme.palette.info.main];

    return (
      <Box
        ref={ref}
        sx={{
          p: 0,
          backgroundColor: 'background.default',
          color: 'text.primary',
          minHeight: '100%',
          position: 'absolute',
          left: '-9999px',
          top: '-9999px',
          width: theme.spacing(100),
          '& *': {
            printColorAdjust: 'exact',
            WebkitPrintColorAdjust: 'exact'
          },
          '@media print': {
            display: 'block',
            position: 'static',
            left: 'auto',
            top: 'auto',
            width: '100%',
            backgroundColor: 'background.default'
          }
        }}
      >
        <style>{`
          @page {
            margin: 0;
          }
          @media print {
            html, body {
              margin: 0;
              padding: 0;
              background-color: ${theme.palette.background.default} !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
          }
        `}</style>
        <Box sx={{ width: '100%', backgroundColor: 'background.default' }}>
          <Box sx={{ height: theme.spacing(10), backgroundColor: 'background.default' }} />
          <Box sx={{ px: 7, pb: 10, backgroundColor: 'background.default' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <NetRisePlainLogo color={theme.palette.primary.main} width={40} height={35} />
              <Typography variant="h2" sx={{ fontWeight: 'bold', color: 'text.primary', ml: 2.5 }}>
                Asset Report
              </Typography>
            </Box>
            <Divider sx={{ mb: 3, backgroundColor: 'primary.main', height: '2px', border: 'none' }} />

            <Box sx={{ mb: 6, pt: 2 }}>
              <Typography variant="h4" sx={sectionTitleStyles(theme)}>
                Asset Details
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  <Box sx={{ minWidth: 220 }}>
                    <Typography variant="body1" color="text.secondary">
                      Asset Name
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {valueOrFallback(asset?.name)}
                    </Typography>
                  </Box>
                  <Box sx={{ minWidth: 220 }}>
                    <Typography variant="body1" color="text.secondary">
                      Risk Score
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <NRHighlightedText
                        color={weightedColor}
                        colorAlt={weightedColorAlt}
                        backgroundColor={weightedColor}
                        textAlphaValue={!isNumber(riskValue) ? 0.56 : 1}
                        highlightAll={true}
                        text={`${isNumber(riskValue) ? `${riskValue} - ` : ''}${weightedCategory}`}
                        alphaValue={0.15}
                        showBorder={true}
                        minWidth={'150px'}
                        justifyContent={'flex-start'}
                        fontSize={12}
                        lineHeight={14}
                      />
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  <Box sx={{ minWidth: 180 }}>
                    <Typography variant="body1" color="text.secondary">
                      Vendor
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {valueOrFallback(asset?.vendor)}
                    </Typography>
                  </Box>
                  <Box sx={{ minWidth: 180 }}>
                    <Typography variant="body1" color="text.secondary">
                      Product
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {valueOrFallback(asset?.product)}
                    </Typography>
                  </Box>
                  <Box sx={{ minWidth: 180 }}>
                    <Typography variant="body1" color="text.secondary">
                      Version
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {valueOrFallback(asset?.version)}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  <Box sx={{ minWidth: 180 }}>
                    <Typography variant="body1" color="text.secondary">
                      Type
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {valueOrFallback(asset?.type)}
                    </Typography>
                  </Box>
                  <Box sx={{ minWidth: 320 }}>
                    <Typography variant="body1" color="text.secondary">
                      SHA256
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {valueOrFallback(asset?.sha256)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box sx={{ mb: 6, pt: 2 }}>
              <Typography variant="h4" sx={sectionTitleStyles(theme)}>
                Top 5 Vulnerabilities by EPSS
              </Typography>
              <SectionTable
                columns={[
                  { key: 'cve', label: 'CVE' },
                  { key: 'epssPercentile', label: 'EPSS %' },
                  { key: 'vendor', label: 'Vendor' },
                  { key: 'version', label: 'Version' }
                ]}
                rows={topVulnsByEPSS.map(vuln => ({
                  ...vuln,
                  epssPercentile: vuln?.epssPercentile ? `${(vuln.epssPercentile * 100).toFixed(2)}%` : '0.00%'
                }))}
              />
            </Box>

            <Box sx={{ mb: 6, pageBreakBefore: 'always', pt: 2 }}>
              <Typography variant="h4" sx={sectionTitleStyles(theme)}>
                Top 5 Threat Actors Weaponized
              </Typography>
              <SectionTable
                columns={[
                  { key: 'cve', label: 'CVE' },
                  { key: 'severity', label: 'Severity' },
                  { key: 'vendor', label: 'Vendor' },
                  { key: 'version', label: 'Version' }
                ]}
                rows={topThreatActorWeaponized}
              />
            </Box>

            <Box sx={{ mb: 6 }}>
              <Typography variant="h4" sx={sectionTitleStyles(theme)}>
                Top 5 Vulnerable Components (by vuln count)
              </Typography>
              <SectionTable
                columns={[
                  { key: 'name', label: 'Name' },
                  { key: 'vendor', label: 'Vendor' },
                  { key: 'version', label: 'Version' },
                  { key: 'total', label: 'Total' }
                ]}
                rows={topVulnerableComponents.map(component => ({
                  ...component,
                  total: component?.vulnerabilities?.total
                }))}
              />
            </Box>

            <Box sx={{ mb: 6, pageBreakBefore: 'always', pt: 2 }}>
              <Typography variant="h4" sx={sectionTitleStyles(theme)}>
                Vulnerabilities Summary
              </Typography>
              <NRComparisonSBOMBox title="Vulnerabilities by Severity" sx={{ flexBasis: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <NRSummaryDonutContainer summaryData={vulnSummary} isPrintView={true} />
                </Box>
              </NRComparisonSBOMBox>
            </Box>

            <Box sx={{ mb: 6 }}>
              <Typography variant="h4" sx={sectionTitleStyles(theme)}>
                Known Exploits
              </Typography>
              {knownExploitsChart.categories.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  No known exploit metrics with a value greater than zero.
                </Typography>
              ) : (
                <Box sx={{ mt: 1, width: '100%' }}>
                  <NRLineChart
                    a11yTitle="Known Exploits"
                    chartContainerID={`known-exploits-${knownExploitsChartContainerId}`}
                    toolbarOffsetY={-45}
                    height={knownExploitsChartHeight}
                    categories={knownExploitsChart.categories}
                    type="bar"
                    colors={knownExploitsBarColors}
                    seriesName="Count"
                    data={knownExploitsChart.data}
                    barWidth="40%"
                    tooltipTheme="dark"
                    disableAnimation
                  />
                </Box>
              )}
            </Box>

            <Box sx={{ mb: 6, pageBreakBefore: 'always', pt: 2 }}>
              <Typography variant="h4" sx={sectionTitleStyles(theme)}>
                Cryptography
              </Typography>
              <NRComparisonSBOMBox title="Cryptography by asset type" sx={{ flexBasis: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <NRCryptographyChart2
                    certs={crypto?.certificates ?? 0}
                    invalidCerts={crypto?.invalidCertificates ?? 0}
                    publicKeys={crypto?.publicKeys ?? 0}
                    publicFoundInPrivate={crypto?.publicFoundInPrivate ?? 0}
                    privateKeys={crypto?.privateKeys ?? 0}
                    privateFoundInPublic={crypto?.privateFoundInPublic ?? 0}
                    disableAnimation
                  />
                </Box>
              </NRComparisonSBOMBox>
            </Box>

            <Box sx={{ mb: 6 }}>
              <Typography variant="h4" sx={sectionTitleStyles(theme)}>
                Misconfigurations
              </Typography>
              <NRComparisonSBOMBox title="Checks by result" sx={{ flexBasis: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <NRMisconfigurationChecks
                    chartContainerID={`chart-misconfig-${misconfigChartContainerId}`}
                    labelsTitle={CONSTANTS.SUMMARY.MISCONFIGURATION_CHECKS}
                    failed={misconfigChartProps.failed}
                    passed={misconfigChartProps.passed}
                    notApplicable={misconfigChartProps.notApplicable}
                    disableAnimation
                  />
                </Box>
              </NRComparisonSBOMBox>
            </Box>

            <Box sx={{ mb: 6, pageBreakBefore: 'always', pt: 2 }}>
              <Typography variant="h4" sx={sectionTitleStyles(theme)}>
                Misconfigurations by Category
              </Typography>
              <SectionTable
                columns={[
                  { key: 'category', label: 'Category' },
                  { key: 'passed', label: 'Passed' },
                  { key: 'failed', label: 'Failed' },
                  { key: 'notApplicable', label: 'N/A' }
                ]}
                rows={misconfigByCat}
              />
            </Box>

            <Box sx={{ mb: 6 }}>
              <Typography variant="h4" sx={sectionTitleStyles(theme)}>
                Licenses
              </Typography>
              <SectionTable
                columns={[
                  { key: 'license', label: 'License' },
                  { key: 'count', label: 'Count' }
                ]}
                rows={licenses}
              />
            </Box>

            <Box sx={{ mb: 6, pageBreakBefore: 'always', pt: 2 }}>
              <Typography variant="h4" sx={sectionTitleStyles(theme)}>
                Cracked Credentials
              </Typography>
              <CrackedCredentialsCards credentials={crackedCredentials} />
            </Box>

            <Box sx={{ pt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Generated on: {printableGeneratedAt}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ height: theme.spacing(10), backgroundColor: 'background.default' }} />
        </Box>
      </Box>
    );
  }
);

export default NRAssetSummaryPrintable;
